// purpose: sim の player.chain 遷移を観測してコンボ体感層（昇格ジングル・BREAK 音・昇格振動）を駆動し、あわせてゲームプレイ SE をリッチ演出へ橋渡しする薄いアダプタ。?r2=1 経路専用・sim へは screenShakeTicks 以外書き戻さない。

import type { AudioEngine, AudioSettings } from '../audio/AudioEngine';
import { CombatAudioEngine, type CombatPhase } from '../audio/r2/CombatAudioEngine';
import { TechnoGameplaySfxDirector } from '../audio/r2/TechnoGameplaySfxDirector';
import type { GameWorld } from '../world/GameWorld';
import { isMobilePerformanceDevice } from '../platform/mobilePerformance';
import { comboAnnouncement, comboTierIndex } from './comboPhase';

/** 昇格の手応え。既存の被弾18・ボス24 より軽く、撃破3/8 より重い位置づけ。 */
const PROMOTION_SHAKE_TICKS = 12;
/** HUD の BREAK 表示と同じ下限（CHAIN 段に届かない 1 で切れても鳴らさない）。 */
const BREAK_MIN_CHAIN = 2;
const NO_CHAIN = 0;

/** comboPhase.ts の段位名。engine 側メソッド名（fever）はハーネス互換のため改名せず、ここで対応づける。 */
const RUSH_NAME = 'RUSH';
const STORM_NAME = 'STORM';
const OVERDRIVE_NAME = 'OVERDRIVE';

const NORMAL_PHASE: CombatPhase = 'normal';
const RUSH_PHASE: CombatPhase = 'rush';
const FEVER_PHASE: CombatPhase = 'fever';
const OVERDRIVE_PHASE: CombatPhase = 'overdrive';

const VOLUME_MIN = 0;
const VOLUME_MAX = 1;
const COMBO_RUSH = 0;
const COMBO_STORM = 1;
const COMBO_OVERDRIVE = 2;
const COMBO_BREAK = 3;

const clampVolume = (value: number): number => Math.max(VOLUME_MIN, Math.min(VOLUME_MAX, value));

export class ComboFeelAdapter {
  private engine: CombatAudioEngine | null = null;
  private pendingEngine: CombatAudioEngine | null = null;
  private sfxDirector: TechnoGameplaySfxDirector | null = null;
  private prevChain = NO_CHAIN;
  private prevTier = 0;
  private prevFormation = 0;
  private masterLevel = VOLUME_MAX;
  private musicLevel = VOLUME_MAX;
  private sfxLevel = VOLUME_MAX;
  private unlocking = false;
  private disposed = false;
  private readonly minimalPresentation = isMobilePerformanceDevice();

  /** Fix 1: shares AudioEngine's single AudioContext instead of opening a second
   *  live one — two concurrent AudioContexts were keeping the audio thread busy
   *  even at silence. */
  constructor(private readonly audio: AudioEngine) {}

  /**
   * 初回ユーザージェスチャで lazy 生成する。App は pointerdown/keydown の両方から
   * 呼ぶため、生成は await をまたがない同期区間で行い AudioContext を二重に作らない。
   * GameRuntime は audio.ensureStarted() を必ず先に await するので、ここに来る
   * 時点で共有コンテキストは既に存在する（無くても unlock 側が単独生成にフォール
   * バックする）。
   */
  async unlock(): Promise<void> {
    if (this.disposed || this.unlocking) return;
    this.unlocking = true;
    const sharedContext = this.audio.getContext();
    let engine = this.engine;
    // AudioEngine can rebuild a stalled WebKit graph after a route change.
    // Rich SFX nodes must follow that new shared context instead of retaining
    // the closed predecessor forever.
    if (engine?.ctx && sharedContext && engine.ctx !== sharedContext) {
      engine.dispose();
      engine = null;
      this.engine = null;
      this.sfxDirector = null;
    }
    const pending = this.pendingEngine;
    if (pending?.ctx && sharedContext && pending.ctx !== sharedContext) {
      pending.dispose();
      this.pendingEngine = null;
    }
    engine ??= this.pendingEngine;
    if (!engine) {
      engine = new CombatAudioEngine();
      this.pendingEngine = engine;
      this.applySettings(engine);
    }
    let unlockError: unknown = null;
    try {
      await engine.unlock(sharedContext ?? undefined);
      if (this.disposed) {
        engine.dispose();
        if (this.pendingEngine === engine) this.pendingEngine = null;
        return;
      }
    } catch (error) {
      unlockError = error;
    } finally {
      this.unlocking = false;
    }
    if (this.disposed) return;

    // AudioEngine can replace its shared context while the 30-file bank is
    // decoding. A concurrent unlock call is deliberately coalesced above, so
    // settle that coalesced request here and rebuild once on the newest graph.
    const latestContext = this.audio.getContext();
    if (latestContext && engine.ctx !== latestContext) {
      if (this.engine === engine) this.engine = null;
      if (this.pendingEngine === engine) this.pendingEngine = null;
      this.sfxDirector = null;
      engine.dispose();
      await this.unlock();
      return;
    }
    if (unlockError) throw unlockError;

    // Do not claim an event was handled until the complete approved sample
    // bank has decoded. Before this point the caller must keep using its
    // existing AudioEngine fallback instead of swallowing shots into null.
    if (this.pendingEngine === engine) this.pendingEngine = null;
    this.engine = engine;
    this.sfxDirector = new TechnoGameplaySfxDirector(engine, this.audio);
  }

  setSettings(settings: AudioSettings): void {
    this.masterLevel = clampVolume(settings.master);
    this.musicLevel = clampVolume(settings.music);
    this.sfxLevel = clampVolume(settings.sfx);
    const engine = this.engine;
    const pendingEngine = this.pendingEngine;
    if (engine) this.applySettings(engine);
    if (pendingEngine && pendingEngine !== engine) this.applySettings(pendingEngine);
  }

  /**
   * 外部コマンド（ゲーム開始・タイトル復帰・ステージ進行）由来の chain リセットを
   * 演出なしで取り込む。ここで鳴らすと操作の副作用が BREAK に化ける。
   */
  sync(world: GameWorld): void {
    this.prevChain = world.player.chain;
    this.prevTier = comboTierIndex(this.prevChain);
    this.prevFormation = world.nodes.formation;
  }

  /** tick 直後に毎フレーム呼ばれる。フレーム毎の割当はゼロ。 */
  observe(world: GameWorld): void {
    const formation = world.nodes.formation;
    if (formation !== this.prevFormation) {
      // 機構の接触点は即時、編隊固有の奏法フレーズは次拍へ置く。
      this.engine?.playTechnoFormation(formation, undefined, .34, .026);
      const at = this.audio.nextR2MusicGridTime(4, .012) ?? undefined;
      this.engine?.playTechnoFormation(formation, at);
      this.prevFormation = formation;
    }

    const chain = world.player.chain;
    if (chain === this.prevChain) return;
    const tier = comboTierIndex(chain);
    if (tier > this.prevTier) this.promote(world, chain);
    else if (tier < this.prevTier) this.demote(chain);
    this.prevChain = chain;
    this.prevTier = tier;
  }

  /**
   * ゲームプレイ SE をリッチ演出へ流す。engine 未生成・未起動なら false を返し、
   * 呼び出し側が旧 AudioEngine.playSfx へフォールバックする（旧経路は不変）。
   */
  playSfx(id: number, chain: number, formation: number): boolean {
    const director = this.sfxDirector;
    if (!director || this.disposed) return false;
    return director.play(id, chain, formation);
  }

  dispose(): void {
    this.disposed = true;
    const engine = this.engine;
    const pendingEngine = this.pendingEngine;
    this.engine = null;
    this.pendingEngine = null;
    this.sfxDirector = null;
    if (engine) engine.dispose();
    if (pendingEngine && pendingEngine !== engine) pendingEngine.dispose();
  }

  private promote(world: GameWorld, chain: number): void {
    const announcement = comboAnnouncement(chain);
    if (!announcement) return;
    if (this.minimalPresentation) {
      if (announcement.name === RUSH_NAME) this.audio.playMobileCombo(COMBO_RUSH);
      else if (announcement.name === STORM_NAME) this.audio.playMobileCombo(COMBO_STORM);
      else if (announcement.name === OVERDRIVE_NAME) this.audio.playMobileCombo(COMBO_OVERDRIVE);
      return;
    }
    world.screenShakeTicks = Math.max(world.screenShakeTicks, PROMOTION_SHAKE_TICKS);
    const engine = this.engine;
    if (!engine) return;
    engine.setPhase(this.phaseForChain(chain));
    const at = this.audio.nextR2MusicGridTime(4, .012) ?? undefined;
    if (at !== undefined) this.audio.duckMusicExternal(.5, .07, Math.max(0, at - .036));
    if (announcement.name === RUSH_NAME) engine.playTechnoCombo(COMBO_RUSH, at);
    else if (announcement.name === STORM_NAME) engine.playTechnoCombo(COMBO_STORM, at);
    else if (announcement.name === OVERDRIVE_NAME) engine.playTechnoCombo(COMBO_OVERDRIVE, at);
  }

  private demote(chain: number): void {
    if (this.minimalPresentation) {
      if (chain === NO_CHAIN && this.prevChain >= BREAK_MIN_CHAIN) this.audio.playMobileCombo(COMBO_BREAK);
      return;
    }
    const engine = this.engine;
    if (!engine) return;
    engine.setPhase(this.phaseForChain(chain));
    if (chain === NO_CHAIN && this.prevChain >= BREAK_MIN_CHAIN) {
      const at = this.audio.nextR2MusicGridTime(4, .012) ?? undefined;
      engine.playTechnoCombo(COMBO_BREAK, at);
    }
  }

  private phaseForChain(chain: number): CombatPhase {
    const announcement = comboAnnouncement(chain);
    if (!announcement) return NORMAL_PHASE;
    if (announcement.name === OVERDRIVE_NAME) return OVERDRIVE_PHASE;
    if (announcement.name === STORM_NAME) return FEVER_PHASE;
    return RUSH_PHASE;
  }

  /**
   * engine の master gain はユーザー承認済リファレンスの定数で改変禁止のため、
   * master 設定はバス側に掛けて反映し、無音指定だけ setMuted に落とす。
   */
  private applySettings(engine: CombatAudioEngine): void {
    engine.setMusicVolume(this.musicLevel * this.masterLevel);
    engine.setSfxVolume(this.sfxLevel * this.masterLevel);
    engine.setMuted(this.masterLevel <= VOLUME_MIN);
  }
}
